import { RecordListType } from '@/types/api';
import Content from '@/components/MobileApproval/Content';
import { strDate, tagToColor } from '@/utils/function';
import { modeToTitle } from '@/components/Approval/utils/functions';
import { t } from 'i18next';
import React, { useEffect, useRef, useState } from 'react';
import TalkFLowModal, {
  TalkFlowModalRef,
} from '@/components/Record/TalkFLowModal';
import {
  EMPTY_SUBJECT,
  getTalkFlowDisplaySubject,
} from '@/components/Record/util/function';
import NoData from '@/components/MobileApproval/NoData';
import { ApprovalContentContainer } from '@/components/MobileApproval/ApprovalContents/styles';
import ShareIcon from '@/public/icons/record/share.svg';

type P = {
  data?: RecordListType[];
  handleBookmark: (docId: string, isBookmarked: boolean) => void;
};

const DocumentContents = ({ data, handleBookmark }: P) => {
  const documentModal = useRef<TalkFlowModalRef>(null);
  const [recordingId, setRecordingId] = useState('');
  const [errorList, setErrorList] = useState<string[]>([]);

  const sendPostMessage = (docId: string) => {
    const sendData = JSON.stringify({
      actionType: 'MOVE_TAB',
      tabName: 'document',
      docId: docId,
    });
    window?.ReactNativeWebView?.postMessage(sendData);
  };

  const handleClick = (docId: string) => {
    if (window?.isRNWebview) {
      sendPostMessage(docId);
    } else {
      documentModal.current?.open(docId);
    }
  };

  const handleMessageEvent = (event: MessageEvent | Event) => {
    if ('data' in event && typeof event.data === 'string' && event.data) {
      try {
        const parsed = JSON.parse(event?.data);
        switch (parsed.type) {
          case 'RECORDING':
            setRecordingId(parsed.docId);
            break;
          case 'RECORD_ERROR_DATA':
            setErrorList(parsed.data);
            break;
        }
      } catch (e) {
        console.error(e);
      }
    }
  };

  useEffect(() => {
    const sendData = JSON.stringify({
      actionType: 'GET_RECORD_DATA',
    });
    window?.ReactNativeWebView?.postMessage(sendData);
  }, []);

  useEffect(() => {
    window.addEventListener('message', handleMessageEvent);
    document.addEventListener('message', (e) => {
      handleMessageEvent(e);
    });

    return () => {
      window.removeEventListener('message', handleMessageEvent);
      document.removeEventListener('message', (e) => handleMessageEvent(e));
    };
  }, []);

  return (
    <ApprovalContentContainer>
      <TalkFLowModal ref={documentModal} />
      {data && data && data.length > 0 ? (
        data.map((item) => {
          const {
            id,
            commentCount,
            documentType,
            subject,
            suggestedSubject,
            createdBy,
            createdAt,
            isBookmarked,
            participants,
            aclCount,
            latestComment,
          } = item;

          const profiles = [
            createdBy.profileImageUrl,
            ...participants.internal
              .filter((p) => p.passportId !== createdBy.passportId)
              .map((p) => p.profileImageUrl),
            ...(participants.external ?? []).map((_) => ''),
          ];
          const author =
            latestComment &&
            `${latestComment.createdBy.detail.name} ${latestComment.createdBy.detail.positionName}`;

          const comment = latestComment && latestComment.content;

          return (
            <Content
              key={`Document-${id}`}
              docId={id}
              totalCount={commentCount}
              filterColor={tagToColor(documentType)}
              filterName={modeToTitle(documentType)}
              subject={getTalkFlowDisplaySubject(
                subject,
                suggestedSubject,
                t(EMPTY_SUBJECT),
              )}
              isRecording={id === recordingId}
              haveErrorRecording={errorList.includes(id)}
              rightTopContent={
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                >
                  <ShareIcon />
                  <div
                    style={{
                      color: 'var(--dk-accent, #06C)',
                      fontSize: '14px',
                      fontWeight: '400',
                      lineHeight: '20px',
                    }}
                  >
                    {aclCount}
                  </div>
                </div>
              }
              profiles={profiles}
              author={`${createdBy.name} ${createdBy.positionName}`}
              departmentName={`${createdBy.divisionName} ${createdBy.departmentName}`}
              createAt={strDate(t, createdAt)}
              commentInfo={{
                comment: comment,
                author: author,
              }}
              handleClick={handleClick}
              isBookmarked={isBookmarked}
              handleBookmark={handleBookmark}
              isLoading={item.documentSummarizedStatus === 'progressSummarized'}
            />
          );
        })
      ) : (
        <NoData />
      )}
    </ApprovalContentContainer>
  );
};
export default DocumentContents;
